import 'core-js/actual/structured-clone'; // required for older browsers
import React from 'react';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import { Roboto } from 'next/font/google';
// bonnet-next
import { withBonnetApp } from '@bonnet/next/app';
import { withCtxMiddleware } from '@bonnet/next/ctx-middleware';
// bonnet-atc
import atcId from '@atc/bonnet-ctx-atc-id';
import { setBonnetReferenceConfiguration } from '@atc/bonnet-reference';
// atc-ui
import { AdLibrariesHeaders } from 'reaxl-ads';
import { AnalyticsEngine } from 'reaxl-analytics';
import { DataIslandProvider } from 'reaxl-context';
import { Provider as FeatureFlagsContextProvider } from 'reaxl-features';
import { OptimizelyPageProvider, addOptimizelyDynamicConfig } from 'reaxl-optimizely';

import '@/scss/atc.scss';
import ConfigContext from '@/contexts/ConfigContext';
import CacheKey from '@/utilities/CacheKey';
import { analyticsProviderHandlers } from '@/analytics';
import { withDataLayer } from '@/analytics/withDataLayer';
import featureConfig from '@/constants/features';
import { withWalletConfigs } from '@/context-middleware';

import {
    analyticsDuck,
    dealerLotDuck,
    dealerLotResultsDuck,
    interestRateDuck,
    inventoryResultsDuck,
    keywordSearchDuck,
    paymentsDuck,
    dealsDuck,
    referenceDuck,
    userDuck,
    currentPageNameDuck,
    pageDetailsDuck,
    recentSearchDuck,
    savedInventoryDuck,
} from '@/ducks';

import {
    FooterContainer,
    OptimizelyAnalyticsListenerContainer,
    HeadContainer,
} from '@/containers';

export const MyWalletContainer = dynamic(() => import('../containers/MyWalletContainer'), { ssr: false });

const EnhancedAnalyticsProvider = withDataLayer(AnalyticsEngine);

// adds a prefix for akamai to route /bonnet-reference calls to collections
setBonnetReferenceConfiguration({
    customBasePath: '/collections',
});

const renderAdLibrariesHeads = ({
    reqUrl,
    mvtFeatureParameters,
    configs,
}) => {
    const {
        ENABLE_AD_LIBRARIES_LOAD_FROM_SERVER,
    } = mvtFeatureParameters;

    const enableAdLibrariesLoadFromServer = !!ENABLE_AD_LIBRARIES_LOAD_FROM_SERVER;
    if (!enableAdLibrariesLoadFromServer) return null;

    const openWrapUrl = configs.site.enableOpenWrap && configs.site.openWrapUrl;
    const prebidPath = CacheKey.add('/resources/js/bundle/prebid.js', false);

    return (
        <AdLibrariesHeaders
            reqUrl={reqUrl}
            openWrapUrl={openWrapUrl}
            prebidPath={prebidPath}
        />
    );
};

const roboto = Roboto({
    weight: ['400', '700', '900'],
    subsets: ['latin'],
    display: 'optional',
});

function Collections(props) {
    const {
        Component,
        pageProps,
        configs,
        mvtParams,
        reqUrl,
        isServerSide,
        optimizelyDynamicConfig = {},
    } = props;
    if (typeof window !== 'undefined' && mvtParams.DISABLE_AUDIOEYE) {
        window.$ae = '';
    }

    return (
        <OptimizelyPageProvider
            disabled={process.env.DISABLE_OPTIMIZELY}
            optimizelyDynamicConfig={optimizelyDynamicConfig}
        >
            <ConfigContext.Provider value={configs}>
                <FeatureFlagsContextProvider flags={{ ...mvtParams, config: featureConfig }}>
                    <EnhancedAnalyticsProvider value={analyticsProviderHandlers} />
                    <DataIslandProvider>
                        <div className={roboto.className}>
                            <HeadContainer mvtParams={mvtParams} />
                            {isServerSide && renderAdLibrariesHeads({
                                reqUrl,
                                mvtFeatureParameters: mvtParams,
                                configs,
                            })}
                            <Component {...pageProps} />
                            <MyWalletContainer />
                            <FooterContainer />
                            <OptimizelyAnalyticsListenerContainer />
                        </div>
                    </DataIslandProvider>
                </FeatureFlagsContextProvider>
            </ConfigContext.Provider>
        </OptimizelyPageProvider>
    );
}

Collections.getInitialProps = async (appCtx) => {
    const { ctx } = appCtx;
    const { publicRuntimeConfig = {} } = getConfig();
    const { environment } = publicRuntimeConfig;
    const {
        req,
        data,
    } = ctx;

    await withCtxMiddleware([
        atcId(),
        addOptimizelyDynamicConfig({
            environment, // update to pass 'qa' for cypress tests
        }),
        withWalletConfigs(),
    ], ctx);

    const mvtParams = {
        LISTINGS_MOBILE_SUPPORT: { enabled: true },
        my_wallet_myatc: { enabled: true }, // hardcoded flag since Optimizely was removed and this value was added to brand configs, which collections does not use
    };

    const forcedFeatures = ctx?.data?.optimizelyDynamicConfig?.forcedFeatures || {};

    for (const [key, value] of Object.entries(forcedFeatures)) {
        if (value && Object.keys(value).length !== 0) {
            mvtParams[key] = {
                enabled: true,
                variables: value,
            };
        } else if (value) {
            mvtParams[key] = {
                enabled: true,
            };
        }
    }

    const configs = {
        site: {
            adsEnabled: true,
            environmentName: 'prod',
            imageServerUrl: 'https://images.autotrader.com',
            thirdPartyEnabled: true,
            ymmtLinksEnabled: true,
            optimizelyWebGlobalEnabled: false,
            optimizelyEdgeGlobalEnabled: false,
            optimizelyWebEnabled: {
                CONDITION: false,
                HOMEPAGE: false,
                MAKE: false,
                MODEL: false,
                STYLE: false,
                TRIM: false,
                DEFAULT: false,
            },
            optimizelyEdgeEnabled: {
                CONDITION: false,
                HOMEPAGE: false,
                MAKE: false,
                MODEL: false,
                STYLE: false,
                TRIM: false,
                DEFAULT: false,
            },
            adsLazyLoadingOffset: -20,
            homeServiceBannerEnabled: true,
        },
        endUserMonitoring: {
            RUMBeaconURL: '//www.autotrader.com/beacon/rum',
            renderNRBrowser: true,
            NRBrowserApplicationId: '61451628',
            NRBrowserLicenseKey: '3136b8c69c',
            renderRUMBeacon: true,
            performResourceTiming: false,
            nrbrowserPageName: 'UCLP',
        },
    };

    const reqUrl = req && `${req.protocol}://${req.get('host')}${req.originalUrl}`;

    const isServerSide = typeof window === 'undefined';
    return {
        configs,
        mvtParams,
        reqUrl,
        isServerSide,
        isBot: data.isBot,
        optimizelyDynamicConfig: ctx.data.optimizelyDynamicConfig,
    };
};

Collections.displayName = '_APP_';

const { BonnetAppComponent } = withBonnetApp(Collections, {
    reducers: [
        analyticsDuck,
        dealerLotDuck,
        dealerLotResultsDuck,
        interestRateDuck,
        inventoryResultsDuck,
        keywordSearchDuck,
        paymentsDuck,
        dealsDuck,
        referenceDuck,
        recentSearchDuck,
        savedInventoryDuck,
        userDuck,
        currentPageNameDuck,
        pageDetailsDuck,
    ],
    modules: [],
});

export default BonnetAppComponent;
